import { useRouter } from 'next/router'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'

import { storeConfiguration } from '@Components/CountryDetectionModal/config'
import styles from '@Components/CountryDetectionModal/styles.module.scss'
import { ImageV4 } from '@Components/Image'
import { NativeModal } from '@Components/Modal/NativeModal/NativeModal'
import { enableBodyScroll } from '@Globals/BodyHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { FlagsUrls } from '@Shared/Statics/Flags'

const WEB_CRAWLERS_REGEX = /bot|google|baidu|bing|msn|duckduckbot|teoma|slurp|yandex/i

export const CountryDetectionModal: FC = () => {
  const { alternateLinks, store } = useContext(MetadataContext)
  const { locale = '' } = useRouter()
  const [cookies, setCookie] = useCookies(['th_country', 'th_country_selector'])
  const [isOpen, setIsOpen] = useState(false)

  const country = store?.config?.countryCode || ''
  const geoIPCountryCode = cookies.th_country_selector || ''

  const data = useMemo(() => {
    const current = storeConfiguration.get(country) || null
    const target = storeConfiguration.get(geoIPCountryCode) || null

    if (!current || !target) {
      return null
    }

    return { current, target }
  }, [country, geoIPCountryCode])

  useEffect(() => {
    if (!country || !data || !geoIPCountryCode) {
      return
    }

    // Check for crawlers
    if (window.navigator && WEB_CRAWLERS_REGEX.test(window.navigator.userAgent)) {
      return
    }

    const currentCookieValue = cookies.th_country
    if (country === currentCookieValue) {
      return
    }
    setCookie('th_country', country, { path: '/' })

    if (country === geoIPCountryCode) {
      return
    }

    setIsOpen(true)
  }, [country, data, geoIPCountryCode, cookies.th_country, setCookie])

  if (!isOpen || !data) {
    return null
  }

  return (
    <NativeModal
      backdrop
      className={styles.modal}
      closeOnBackDrop={false}
      closeOnEscape={false}
      defaultOpen
      onClose={() => {
        setIsOpen(false)
        enableBodyScroll()
      }}
    >
      <span className={styles.title}>{data.target.available}</span>
      <button
        className={styles.goToButton}
        color="dark"
        onClick={() => {
          const alternateLink = alternateLinks.find(link => link.locale === data.target.locale)
          const redirectURL = new URL((alternateLink && alternateLink.url) || data.target.url)

          // apply current search parameters to redirect url
          new URLSearchParams(window.location.search).forEach((value, key) => {
            redirectURL.searchParams.set(key, value)
          })

          redirectURL.searchParams.set('th_source_locale', locale)

          window.location.href = redirectURL.toString()
        }}
        type="button"
      >
        {data.target.goTo.split('%s')[0]}
        <span>
          <ImageV4
            alt={geoIPCountryCode}
            className="buttonImage"
            height={32}
            src={FlagsUrls[geoIPCountryCode.toLowerCase()]}
            width={32}
          />
          {data.target.url.split('www.')[1]}
        </span>
      </button>
      <button
        className={styles.stayButton}
        onClick={() => {
          setIsOpen(false)
          enableBodyScroll()
        }}
        type="button"
      >
        {data.target.stayAt.split('%s')[0]}
        <b className="buttonText">{data.current.url.split('www.')[1]}</b>
        {data.target.stayAt.split('%s')[1]}
      </button>
    </NativeModal>
  )
}
